/**
 * Settings: a nav of sections down the left, one pane at a time, and one save for all of it.
 *
 * BUILT FROM "SETTINGS V2" IN CLAUDE DESIGN (2026-09-26), which replaced the tab strip. What
 * the prototype decided and this keeps: the grouped nav (Business, Agent, Calls, People, and
 * Our team only for an internal reader), a heading and one sentence per pane, cards of
 * label-and-control rows, one floating bar that saves everything, Try your agent in the head
 * as a drawer, and a tool editor that takes the whole screen. Where this differs, and why:
 *   - "Our team only" where the prototype said "clients don't see this" (the terms table).
 *   - Hours as a card per location with dialogs, no re-sort range, no "Hear default", connections still in a dialog,
 *     and every request option the old tool dialog had - each explained in its own file.
 *   - Members keep adding and removing people themselves; there is no internal Team access
 *     pane, and a custom agent's owner is not told it is custom.
 *
 * TWO PAGES ARE FETCHED, AND THE SCREEN WAITS FOR BOTH. `/v1/settings` is the business and
 * its people; `/v1/agent` is what the agent says and does, and the only place its runtime,
 * greeting recording and tools come from. The draft is seeded from the two together, so a
 * pane never renders against half of it.
 *
 * THE SECTION IS THE URL (`?tab=`), normalised on read and never redirected, with the old
 * tab names as aliases - see `sections.ts`. `replace`, because a section is not a
 * destination: Back leaves Settings rather than walking back through the panes.
 *
 * THE DRAFT IS NOT HERE. It is `settings/draft.tsx`, above every pane, so a half-typed field
 * survives switching sections - the guarantee the old form bought by holding every field in
 * one component, and the one the greeting and the instructions used not to have.
 */

import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApi, type AgentPage, type SettingsPage } from "../api";
import { State } from "../components/Internal";
import { Empty } from "../components/States";
import { PageHead } from "../components/Page";
import { Mic } from "../components/Icons";
import { useSession } from "../session";
import { useAgent, useCurrentAgent } from "../currentAgent";
import { when } from "../format";
import { cx } from "../ui";
import { FIRST, groupsFor, resolveSection, type SectionId } from "../components/settings/sections";
import { LEAVE_QUESTION, PART_SECTION, SettingsDraftProvider, setLeaveGuard, useDraft } from "../components/settings/draft";
import { PaneHead, SettingsNav, type Marks } from "../components/settings/SettingsNav";
import { SaveBar } from "../components/settings/SaveBar";
import { DetailsPane } from "../components/settings/panes/DetailsPane";
import { HoursPane } from "../components/settings/panes/HoursPane";
import { GreetingPane } from "../components/settings/panes/GreetingPane";
import { InstructionsPane } from "../components/settings/panes/InstructionsPane";
import { ToolsPane } from "../components/settings/panes/ToolsPane";
import { AgentTypePane, LanguagesPane } from "../components/settings/panes/SmallPanes";
import { KnowledgePane } from "../components/settings/panes/KnowledgePane";
import { TrackingPane } from "../components/settings/panes/TrackingPane";
import { TeamPane } from "../components/settings/panes/TeamPane";
import { VoicePane } from "../components/settings/panes/VoicePane";
import { DemoPagePane } from "../components/settings/panes/DemoPagePane";
import { Handset, TryItDrawer } from "../components/agent/TryItDrawer";

export { sameLocations } from "../components/settings/draft";

/** The track the page runs in, the prototype's 1180px less nothing. */
const TRACK = "mx-auto w-full max-w-[1180px]";

export function Settings() {
  const { me } = useSession();
  // Both halves at once: the address named the agent, so neither waits for the other.
  const agentId = encodeURIComponent(useAgent().id);
  const settings = useApi<SettingsPage>(`/v1/settings?agent=${agentId}`);
  const page = useApi<AgentPage>(`/v1/agent?agent=${agentId}`);
  const reloadSettings = settings.reload;
  const reloadPage = page.reload;
  const reload = useCallback(() => {
    reloadSettings();
    reloadPage();
  }, [reloadSettings, reloadPage]);

  if (page.error) {
    return (
      <>
        <Head />
        <div className={TRACK}>
          <Empty icon={Mic} title="This could not be loaded" body={page.error.message} action={{ label: "Try again", onClick: reload }} />
        </div>
      </>
    );
  }

  if (!settings.data || !page.data) {
    return (
      <>
        <Head />
        <div className={TRACK}>
          <State loading={settings.loading || page.loading} error={settings.error} empty={false} shape="panels" />
        </div>
      </>
    );
  }

  return <AgentSettings data={settings.data} agentPage={page.data} me={me?.user.email ?? ""} onSaved={reload} />;
}

/**
 * The page once both halves have landed. Exported for the tests, which mount it with fixtures
 * and a stubbed `fetch` rather than the page around a fetch.
 */
export function AgentSettings({
  data,
  agentPage,
  me,
  onSaved,
}: {
  data: SettingsPage;
  agentPage: AgentPage;
  me: string;
  onSaved: () => void;
}) {
  const { reload: reloadCurrentAgent } = useCurrentAgent();
  return (
    <SettingsDraftProvider key={data.agent.id} settings={data} page={agentPage} onSaved={onSaved} onAgentChanged={reloadCurrentAgent}>
      <Screen data={data} agentPage={agentPage} me={me} onSaved={onSaved} />
    </SettingsDraftProvider>
  );
}

function Screen({ data, agentPage, me, onSaved }: { data: SettingsPage; agentPage: AgentPage; me: string; onSaved: () => void }) {
  const agent = data.agent;
  const { dirty, state } = useDraft();
  const reader = { runtime: agentPage.agent.runtime, internal: data.user.role === "internal" };
  const groups = groupsFor(reader);
  const [params, setParams] = useSearchParams();
  const section = resolveSection(params.get("tab"), reader);
  const [trying, setTrying] = useState(false);
  const hosted = agentPage.agent.runtime === "hosted";

  const pick = (id: SectionId) =>
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (id === FIRST) next.delete("tab");
        else next.set("tab", id);
        next.delete("tool");
        return next;
      },
      { replace: true },
    );

  useLeaveGuard(dirty.length > 0);

  const marks: Marks = {};
  for (const part of dirty) marks[PART_SECTION[part]] = "dirty";
  for (const part of Object.keys(state.errors) as Array<keyof typeof state.errors>) marks[PART_SECTION[part]] = "error";

  const tools = agentPage.tools ?? [];
  const tags = {
    ...(agentPage.knowledgeSources ? { knowledge: String(agentPage.knowledgeSources) } : {}),
    ...(hosted ? { tools: String(tools.filter((tool) => state.draft.tools[tool.id] ?? tool.enabled).length) } : {}),
  };

  const change = agentPage.lastChange ?? (agentPage.agent.changedAt ? { at: agentPage.agent.changedAt, by: agentPage.agent.changedBy } : null);

  return (
    <>
      <Head
        lede={
          <>
            {change && `Last changed ${when(change.at)}${change.by ? ` by ${change.by}` : ""}. `}
            <span className="text-ink-faint">Saved changes apply from the next call.</span>
          </>
        }
        actions={
          hosted && agentPage.tryIt ? (
            <button
              type="button"
              disabled={!agentPage.tryIt.available}
              title={agentPage.tryIt.available ? undefined : agentPage.tryIt.reason}
              onClick={() => setTrying(true)}
              className="flex h-10.5 items-center gap-2.5 rounded-full bg-ink pr-4.5 pl-3.5 text-base font-medium text-paper transition-colors hover:bg-ink-soft disabled:opacity-55"
            >
              <span className="grid size-6 place-items-center rounded-full bg-accent text-accent-ink">
                <Handset size={12} />
              </span>
              Try your agent
            </button>
          ) : undefined
        }
      />

      <div className={cx(TRACK, "grid grid-cols-[208px_minmax(0,1fr)] items-start gap-10 pb-36", "max-[1000px]:grid-cols-1 max-[1000px]:gap-5")}>
        <SettingsNav groups={groups} current={section} marks={marks} tags={tags} onPick={pick} />

        <div className="grid min-w-0 gap-5">
          <PaneHead id={section} />
          {section === "details" && <DetailsPane agent={agent} />}
          {section === "hours" && <HoursPane />}
          {section === "greeting" && <GreetingPane agentId={agent.id} block={agentPage.greeting} onRecorded={onSaved} />}
          {section === "instructions" && agentPage.instructions && (
            <InstructionsPane block={agentPage.instructions} tools={agentPage.tools ?? []} />
          )}
          {section === "tools" && <ToolsPane agentId={agent.id} page={agentPage} onChanged={onSaved} />}
          {section === "knowledge" && <KnowledgePane agentId={agent.id} internal={reader.internal} onChanged={onSaved} />}
          {section === "tracking" && <TrackingPane agent={agent} onQueued={onSaved} />}
          {section === "team" && <TeamPane data={data} me={me} onChanged={onSaved} />}
          {section === "languages" && <LanguagesPane languages={agent.languages} />}
          {section === "voice" && <VoicePane agentId={agent.id} voice={agent.voice} />}
          {section === "agenttype" && <AgentTypePane />}
          {section === "demopage" && <DemoPagePane agentId={agent.id} agentSlug={agent.slug} />}
        </div>
      </div>

      <SaveBar hidden={Boolean(params.get("tool"))} onShow={pick} />

      {trying && agentPage.tryIt && (
        <TryItDrawer
          agentId={agent.id}
          agentName={agentPage.agent.name}
          remaining={agentPage.tryIt.remaining}
          perHour={agentPage.tryIt.perHour}
          unsaved={dirty.length > 0}
          onClose={() => setTrying(false)}
        />
      )}
    </>
  );
}

function Head({ lede, actions }: { lede?: React.ReactNode; actions?: React.ReactNode }) {
  return <PageHead contain={TRACK} title="Settings" lede={lede ?? "Your business, what your agent says and can do, and who can sign in."} actions={actions} />;
}

/**
 * Ask before a click in the top bar throws the draft away.
 *
 * `beforeunload` covers a reload and a closed tab (the provider sets it). An SPA navigation is
 * not an unload, and the router's `useBlocker` needs a data router this app does not mount. So
 * while something is unsaved: a capture-phase click listener asks before any link that leaves
 * /settings, and the agent switcher asks through `confirmLeave` (it navigates without a link).
 */
function useLeaveGuard(active: boolean) {
  useEffect(() => {
    if (!active) return;
    const ask = () => window.confirm(LEAVE_QUESTION);
    setLeaveGuard(ask);
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
      const link = (event.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!link || link.target === "_blank") return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || url.pathname === window.location.pathname) return;
      if (!ask()) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    document.addEventListener("click", onClick, true);
    return () => {
      setLeaveGuard(null);
      document.removeEventListener("click", onClick, true);
    };
  }, [active]);
}
